/**
 * "Who wants Mumbai" — demand by city, banded by festival.
 *
 * ── THE TWO THINGS THIS PANEL EXISTS TO SAY ─────────────────────────────────
 *
 * 🔴 **1. A CITY FIGURE IS EXPLICIT PICKS PLUS WHOLE-TOUR TAKERS.** An
 * `all_cities` respondent stores no city rows at all, so a count of the named
 * picks alone is LOW and reads as less interest. Both halves are therefore
 * separate COLUMNS as well as a stated sentence — a reader who never reads the
 * sentence still sees the composition in the headings.
 *
 * 🔴 **2. INSTITUTIONS NOBODY HAS APPROVED YET ARE COUNTED SEPARATELY.** A
 * submission attaches before its company is approved, so these figures include
 * institutions nobody has looked at. That is correct and it must be visible;
 * the split is two columns rather than a note under a total, because a single
 * blended number is the one that gets quoted.
 *
 * Both sentences live in `event-interest-wording.ts` and both are the
 * documented **fact about the data the reader cannot otherwise see** exception
 * to the no-helper-text prohibition. ⚠️ **A sweep must not delete either.**
 *
 * ── WHAT THIS PANEL REFUSES TO DO ───────────────────────────────────────────
 *
 * ⛔ **IT SUMS NOTHING ACROSS ROWS.** One institution wanting three cities is
 * three rows, so a column total counts it three times — and the result looks
 * exactly like a number of institutions. A festival-level figure has to come
 * from the server; until one does, the band header states how many CITIES it
 * covers, which is a count of rows and is safe.
 *
 * ⛔ **AND IT DERIVES NO FIGURE OF ITS OWN.** Every number rendered here is a
 * field on the row. The panel's job is to arrange them and to say what they
 * mean; the arithmetic is the server's, and a second answer computed in a
 * browser is the second writer this repo refuses everywhere.
 *
 * ── BANDING ─────────────────────────────────────────────────────────────────
 *
 * ⚠️ **Rows are grouped by festival id into first-appearance order, NOT by
 * collecting consecutive runs.** Consecutive-run banding is what
 * `/client-deliverables` uses and it is correct there because the server leads
 * every sort with the band key. Nothing here controls the demand report's
 * ordering, and a run-based grouping over interleaved rows shatters each
 * festival into a string of one-row bands — with no error, on a screen whose
 * whole shape is the banding.
 */

import { Fragment, type ReactNode } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { pluralise } from '@/lib/format';
import { CityDemandIcon } from './icons';
import {
  CITY_DEMAND_COMPOSITION,
  DEMAND_LABELS,
  NO_CLIENT_SIDE_DEMAND_TOTAL,
  UNAPPROVED_SPLIT_NOTE,
} from './event-interest-wording';

/**
 * One city on one festival's tour, with the demand recorded against it.
 *
 * ⚠️ **A VIEW MODEL, DELIBERATELY, AND NOT THE WIRE TYPE.** The page maps
 * `EventInterestDemandRow` onto this in one adapter, so every field name this
 * feature reads off the response is spelled in exactly one place. It is the
 * `projectsOf` idiom this codebase already uses: three call sites each
 * spelling a field is three chances to disagree, and a renderer reading the
 * wrong one of two structurally identical count fields produces a plausible
 * wrong number with a `200` behind it.
 */
export interface DemandCityRow {
  /** Stable across renders: one city appears under several festivals. */
  key: string;
  festivalId: number;
  festivalName: string;
  cityId: number;
  cityName: string;
  /**
   * Institutions that NAMED this city.
   *
   * ⛔ **On its own this is not demand for the city** — see
   * {@link CITY_DEMAND_COMPOSITION}. It is rendered beside `wholeTour` and
   * never instead of it.
   */
  explicit: number;
  /**
   * Institutions that asked for the whole tour of this festival, and therefore
   * for this city, **without storing a row naming it**.
   */
  wholeTour: number;
  /**
   * The institutions interested in this city.
   *
   * ⚠️ **`explicit` and `wholeTour` PARTITION this by construction**: naming
   * cities alongside `all_cities` is a 422 (contract §5), so no institution is
   * in both halves of one row.
   */
  institutions: number;
  /**
   * How many of {@link institutions} nobody has approved.
   *
   * 🔴 **A SUBSET OF THE TOTAL, NEVER A FOURTH POPULATION TO ADD.** A screen
   * adding this back on reports more interest than exists. `0` is a real
   * answer meaning every interested institution is approved.
   */
  notApproved: number;
}

/**
 * The columns, in order, with the width each contributes to the table's floor.
 *
 * ✅ **ONE ARRAY DRIVES THE HEADER, THE BODY, THE SKELETON AND THE MINIMUM
 * WIDTH.** `/invoices` records what four parallel lists cost: a skeleton cell
 * outliving its column shifts every column after it as the rows arrive, and a
 * hand-carried `min-w-[…]` literal sat stale by 16px for nine days. None of
 * that errors and none of it is visible to `tsc`.
 */
interface DemandColumn {
  key: keyof DemandCityRow | 'city';
  label: string;
  width: number;
  numeric: boolean;
  /** Rendered from the row; kept beside the label so the two cannot drift. */
  value: (row: DemandCityRow) => ReactNode;
  /** Muted where the column qualifies the headline rather than being it. */
  secondary?: boolean;
}

const DEMAND_COLUMNS: readonly DemandColumn[] = [
  {
    key: 'city',
    label: DEMAND_LABELS.city,
    width: 220,
    numeric: false,
    value: (row) => <span className="text-sm text-fg">{row.cityName}</span>,
  },
  /*
   * ⚠️ **THE TOTAL COMES FIRST, BEFORE ITS TWO DECOMPOSITIONS.** It is the
   * answer to the question somebody opened this page with; the columns after
   * it qualify it. Putting `Named it` first would make the under-counting
   * figure the one a reader takes away.
   */
  {
    key: 'institutions',
    label: DEMAND_LABELS.institutions,
    width: 130,
    numeric: true,
    value: (row) => <Figure value={row.institutions} />,
  },
  {
    key: 'explicit',
    label: DEMAND_LABELS.explicit,
    width: 120,
    numeric: true,
    secondary: true,
    value: (row) => <Figure value={row.explicit} muted />,
  },
  {
    key: 'wholeTour',
    label: DEMAND_LABELS.wholeTour,
    width: 130,
    numeric: true,
    secondary: true,
    value: (row) => <Figure value={row.wholeTour} muted />,
  },
  {
    key: 'notApproved',
    label: DEMAND_LABELS.notApproved,
    width: 150,
    numeric: true,
    secondary: true,
    /*
     * ⚠️ **A ZERO IS PRINTED AS `0`, NOT AS A DASH.** An em dash on this column
     * reads as "not measured", and the difference between *every interested
     * institution is approved* and *we did not look* is the whole reason this
     * column exists.
     */
    value: (row) => <Figure value={row.notApproved} muted />,
  },
];

const TABLE_MIN_WIDTH = DEMAND_COLUMNS.reduce((total, column) => total + column.width, 0);
const SKELETON_BANDS = 2;
const SKELETON_ROWS_PER_BAND = 3;

/**
 * A count.
 *
 * ⚠️ **`tabular` so a column of figures lines up**, and the muted step marks a
 * qualifying column apart from the headline without using colour as the only
 * signal — the heading already says which is which.
 */
function Figure({ value, muted }: { value: number; muted?: boolean }) {
  return (
    <span className={muted ? 'tabular text-sm text-fg-muted' : 'tabular text-sm font-medium text-fg'}>
      {value}
    </span>
  );
}

/**
 * Rows grouped by festival, in the order each festival is first seen.
 *
 * A `Map` rather than a run-collector — see the file header for why the two are
 * not interchangeable here.
 */
function bandsOf(rows: readonly DemandCityRow[]) {
  const bands = new Map<number, { festivalId: number; festivalName: string; rows: DemandCityRow[] }>();
  for (const row of rows) {
    const existing = bands.get(row.festivalId);
    if (existing) {
      existing.rows.push(row);
      continue;
    }
    bands.set(row.festivalId, {
      festivalId: row.festivalId,
      festivalName: row.festivalName,
      rows: [row],
    });
  }
  return Array.from(bands.values());
}

/**
 * The two sentences, rendered once at the top of the panel.
 *
 * ⛔ **NEITHER IS HELPER TEXT AND A SWEEP MUST NOT TAKE EITHER** — both state a
 * fact about the data that no figure on this screen can convey, which is the
 * documented exception. See `event-interest-wording.ts`, where the reasoning
 * for each is written at the constant.
 *
 * ⚠️ **`short` on screen and `detail` on the `title`**, the shape this app
 * already uses for the commission visibility note: a line carries one sentence,
 * and the half a reader needs when they stop to ask survives rather than being
 * dropped for width.
 */
function DemandNotes() {
  return (
    <div className="flex flex-col gap-1 border-b border-border px-4 py-2.5 text-xs text-fg-muted">
      <p className="m-0 [overflow-wrap:anywhere]" title={CITY_DEMAND_COMPOSITION.detail}>
        {CITY_DEMAND_COMPOSITION.short}
      </p>
      <p className="m-0 [overflow-wrap:anywhere]" title={UNAPPROVED_SPLIT_NOTE.detail}>
        {UNAPPROVED_SPLIT_NOTE.short}
      </p>
    </div>
  );
}

/**
 * The band heading.
 *
 * ⚠️ **It states a count of CITIES, never of institutions.** A count of rows is
 * safe to compute here; a count of institutions is not, because one institution
 * spans several rows — see {@link NO_CLIENT_SIDE_DEMAND_TOTAL}.
 */
function BandHeading({ name, cityCount }: { name: string; cityCount: number }) {
  return (
    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="text-sm font-semibold text-fg">{name}</span>
      <span className="text-xs font-normal text-fg-muted" title={NO_CLIENT_SIDE_DEMAND_TOTAL}>
        {pluralise(cityCount, 'city', 'cities')}
      </span>
    </span>
  );
}

export interface DemandPanelProps {
  rows: readonly DemandCityRow[];
  loading: boolean;
  /** The server's own sentence, or ours naming what failed. Never invented. */
  error: string | null;
  /** True when a control has narrowed what was asked for. */
  filtered: boolean;
  onClearFilters: () => void;
}

export function DemandPanel({
  rows,
  loading,
  error,
  filtered,
  onClearFilters,
}: DemandPanelProps) {
  const bands = bandsOf(rows);
  const nothingToShow = !loading && rows.length === 0;

  return (
    <Panel>
      <PanelHeader title="Demand by city" />

      {/*
        ⚠️ **THE ERROR IS A `danger`-TONED BLOCK NAMING WHAT FAILED**, above the
        content rather than instead of it, and it carries the server's own
        sentence rather than a house paraphrase. `role="alert"` so it is
        announced when it arrives rather than only when somebody tabs into it.
      */}
      {error && (
        <div className="border-b border-border px-4 py-3">
          <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load demand.</>}>
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        </div>
      )}

      {/*
        The notes render even while the figures are loading and even when there
        are none: they describe how this surface COUNTS, which is true before
        any row arrives, and hiding them until rows land would mean the first
        thing a reader ever sees is a number with no caveat on it.
      */}
      <DemandNotes />

      {nothingToShow ? (
        <DemandEmptyState filtered={filtered} hasError={error !== null} onClear={onClearFilters} />
      ) : (
        <>
          {/* ── Below `md`: a card per city ─────────────────────────────────
              Six columns will not fit 320px. Every one of them is on the card:
              a figure that exists only above a breakpoint is indistinguishable
              from one that was removed, and this app has had a working control
              reported as deleted for exactly that. */}
          <div className="md:hidden">
            {loading ? (
              <SkeletonCards />
            ) : (
              bands.map((band) => (
                <section key={band.festivalId} className="border-b border-border last:border-b-0">
                  <h3 className="m-0 bg-surface-sunken px-4 py-2">
                    <BandHeading name={band.festivalName} cityCount={band.rows.length} />
                  </h3>
                  <ul className="m-0 list-none p-0">
                    {band.rows.map((row) => (
                      <li key={row.key} className="border-t border-border px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                            {row.cityName}
                          </p>
                          {/*
                            The headline figure keeps its own label on a card:
                            a bare number beside a city name is not obviously a
                            count of institutions, and three figures below it
                            would be unreadable without names. **A label names
                            a value rather than explaining it**, so it is not
                            helper text.
                          */}
                          <Badge tone="neutral" variant="outline">
                            {pluralise(row.institutions, 'institution')}
                          </Badge>
                        </div>
                        <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                          <dt className="m-0 text-fg-muted">{DEMAND_LABELS.explicit}</dt>
                          <dd className="m-0 text-right text-fg-secondary tabular">{row.explicit}</dd>
                          <dt className="m-0 text-fg-muted">{DEMAND_LABELS.wholeTour}</dt>
                          <dd className="m-0 text-right text-fg-secondary tabular">{row.wholeTour}</dd>
                          <dt className="m-0 text-fg-muted">{DEMAND_LABELS.notApproved}</dt>
                          <dd className="m-0 text-right text-fg-secondary tabular">
                            {row.notApproved}
                          </dd>
                        </dl>
                      </li>
                    ))}
                  </ul>
                </section>
              ))
            )}
          </div>

          {/* ── From `md`: the table ────────────────────────────────────────
              ONE mount, with only the `<TBody>` swapping. Two branches would
              mean two header rows and, with them, two measured layouts. */}
          <div className="hidden md:block">
            <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
              {/*
                ⚠️ **THE FLOOR GOES THROUGH `style`, NOT A CLASS.** Tailwind v4
                scans SOURCE TEXT, so an interpolated `min-w-[…]` emits no rule
                at all — the class lands in the DOM and does nothing, which
                reads as a table that chose not to hold its width.
              */}
              <Table style={{ minWidth: TABLE_MIN_WIDTH }}>
                <THead>
                  <TR>
                    {DEMAND_COLUMNS.map((column) => (
                      <TH key={column.key} numeric={column.numeric}>
                        {column.label}
                      </TH>
                    ))}
                  </TR>
                </THead>
                {loading ? (
                  <TBody>
                    {Array.from({ length: SKELETON_BANDS * SKELETON_ROWS_PER_BAND }, (_, index) => (
                      <TR key={index}>
                        {DEMAND_COLUMNS.map((column) => (
                          <TD key={column.key} numeric={column.numeric}>
                            <Skeleton
                              className={column.numeric ? 'ml-auto h-3 w-10' : 'h-3 w-32'}
                            />
                          </TD>
                        ))}
                      </TR>
                    ))}
                  </TBody>
                ) : (
                  bands.map((band) => (
                    /*
                     * A `<tbody>` per band, so the heading row belongs to the
                     * rows beneath it structurally rather than by proximity —
                     * which is also what lets a screen reader announce the
                     * festival before its cities.
                     */
                    <TBody key={band.festivalId}>
                      <TR>
                        <TD colSpan={DEMAND_COLUMNS.length} className="bg-surface-sunken">
                          <BandHeading name={band.festivalName} cityCount={band.rows.length} />
                        </TD>
                      </TR>
                      {band.rows.map((row) => (
                        <TR key={row.key}>
                          {DEMAND_COLUMNS.map((column) => (
                            <TD key={column.key} numeric={column.numeric}>
                              {column.value(row)}
                            </TD>
                          ))}
                        </TR>
                      ))}
                    </TBody>
                  ))
                )}
              </Table>
            </TableWrap>
          </div>
        </>
      )}
    </Panel>
  );
}

/** Skeletons in the SHAPE of the real cards, never a spinner. */
function SkeletonCards() {
  return (
    <>
      {Array.from({ length: SKELETON_BANDS }, (_, band) => (
        <Fragment key={band}>
          <div className="border-b border-border bg-surface-sunken px-4 py-2">
            <Skeleton className="h-3 w-40" />
          </div>
          {Array.from({ length: SKELETON_ROWS_PER_BAND }, (_, row) => (
            <div key={row} className="border-b border-border px-4 py-3 last:border-b-0">
              <div className="flex flex-col gap-2">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-2.5 w-40" />
              </div>
            </div>
          ))}
        </Fragment>
      ))}
    </>
  );
}

/**
 * ⛔ **NO COPY HERE IMPLIES DATA IS ON ITS WAY.** A blank report is an
 * ordinary, permanent state — the form runs for one season and nobody may have
 * answered yet — and an empty state that hedges about it is how a dead endpoint
 * gets mistaken for a quiet week. `GET /deliverables` returned a `500` for a day
 * and rendered exactly its documented empty state.
 */
function DemandEmptyState({
  filtered,
  hasError,
  onClear,
}: {
  filtered: boolean;
  hasError: boolean;
  onClear: () => void;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<CityDemandIcon />}
        title="Nothing to show"
        /* Points at the banner above rather than restating the server's
           message — one copy of a refusal, in the place that has all of it. */
        description="Demand couldn’t be loaded. Check the message above and try again."
      />
    );
  }

  return (
    <EmptyState
      icon={<CityDemandIcon />}
      title={filtered ? 'No city matches those filters' : 'No interest recorded yet'}
      description={
        filtered
          ? 'Clear the filters to see every festival and city.'
          : 'Nobody has answered the interest form yet. Cities appear here as institutions submit it.'
      }
      action={
        filtered ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        ) : null
      }
    />
  );
}
